import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { delay, filter, map, skip, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ObjectChoiceService } from 'src/app/object-choice/object-choice.service';
import { BackendService } from '../core/backend.service';
import { LanguageChanged, ResetObjectSelection } from './actions';
import { KeepAlive } from './cart-store/actions';
import { selectCatalogClient } from './catalog-store/selectors';
import * as TreeActions from './object-tree-store/actions';
import { selectTree } from './object-tree-store/selects';
import { RootState } from './root-state.model';
import { selectIsAdminRoute } from './router/selectors';
import { SetTimePickerValues } from './time-picker-store/actions';
import * as UserActions from './user-store/actions';

@Injectable()
export class RootStoreEffects {

  constructor(
    private actions$: Actions,
    private store: Store<RootState>,
    private backend: BackendService,
    private meta: Meta,
    private objectChoiceService: ObjectChoiceService
  ) { }

  resetObjectSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetObjectSelection),
      tap(() => {
        this.objectChoiceService.smallScreenStepIndex.set(1)
      }),
      switchMap(() => [
        TreeActions.SetSelectedItems({ items: [] }),
        SetTimePickerValues({
          start: null,
          end: null,
          preparationMinutes: undefined,
          followUpMinutes: undefined,
          availabilityPlanId: null,
          recurrenceFreq: null,
          quantity: 1
        })
      ])
    )
  )

  keepAlive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KeepAlive),
      switchMap(() => this.backend.keepAlive().pipe(
        delay(60000),
        map(() => KeepAlive())
      ))
    )
  )

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      // wait until catalog is loaded (handled by RouteResolver)
      switchMap(() => this.store.select(selectCatalogClient).pipe(filter(c => !!c))),
      withLatestFrom(this.store.select(selectIsAdminRoute)),
      tap(([client, isAdminRoute]) => {
        // Load user
        this.store.dispatch(UserActions.LoadUser())

        this.meta.addTag({ name: 'apple-mobile-web-app-title', content: client.organization })

        if (!isAdminRoute) {
          // Run KeepAlive after some time
          setTimeout(() => this.store.dispatch(KeepAlive()), 60000)

          // Run KeepAlive when window is active (has focus), to ensure login status (XSRF token) is always up to date
          window.addEventListener('focus', () => this.store.dispatch(KeepAlive()))

          // Load ObjectTree
          this.objectChoiceService.loadTreeItems(this.store)
        }
      })
    ),
    { dispatch: false }
  )

  // update some data when the login state changes
  loginChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.SetUser),
      skip(1),
      withLatestFrom(this.store.select(selectTree)),
      tap(([, tree]) => {
        // if Tree contains data, update it (no mather what UI is currently shown)
        if (tree)
          this.objectChoiceService.loadTreeItems(this.store)
      })
    ),
    { dispatch: false }
  )

  // update some data when the language changes
  languageChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageChanged),
      skip(1),
      withLatestFrom(this.store.select(selectTree)),
      tap(([, tree]) => {
        // if Tree contains data, update it (no mather what UI is currently shown)
        if (tree)
          this.objectChoiceService.loadTreeItems(this.store)
      })
    ),
    { dispatch: false }
  )
}
